import React, { useState, useEffect } from 'react';

export default function InteractiveModal({ open, onClose, node, onSave }) {
  const [formData, setFormData] = useState({
    headerType: 'text', // 'text' or 'media'
    headerText: '',
    messageBody: '',
    footerText: '',
    delay: 0
  });

  useEffect(() => {
    if (open && node) {
      setFormData({
        headerType: node.data.headerType || 'text',
        headerText: node.data.headerText || '',
        messageBody: node.data.messageBody || '',
        footerText: node.data.footerText || '',
        delay: node.data.delay || 0
      });
    }
  }, [open, node]);

  const handleSave = () => {
    if (onSave) {
      onSave(node.id, formData);
    }
    onClose();
  };

  if (!open) return null;

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  };

  const contentStyle = {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '600px',
    maxWidth: '700px',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  };

  const inputStyle = {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '14px',
    marginBottom: '12px',
    outline: 'none'
  };

  const textareaStyle = {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '12px',
    fontSize: '14px',
    marginBottom: '12px',
    outline: 'none',
    minHeight: '100px',
    resize: 'vertical',
    fontFamily: 'inherit'
  };

  const buttonStyle = {
    background: '#007bff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    marginRight: '8px'
  };

  const secondaryButtonStyle = {
    background: '#6c757d',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer'
  };

  const radioButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    cursor: 'pointer'
  };

  const radioCircleStyle = {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid #d1d5db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  };

  const selectedRadioStyle = {
    ...radioCircleStyle,
    border: '2px solid #007bff',
    background: '#007bff'
  };

  const dynamicButtonStyle = {
    background: '#f8f9fa',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '6px 12px',
    fontSize: '13px',
    cursor: 'pointer',
    marginRight: '8px',
    marginBottom: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px'
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#495057', fontSize: '20px', fontWeight: '600' }}>
            Configure Interactive
          </h2>
        </div>

        {/* Message Header Section */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Message Header (Optional)
          </label>
          
          {/* Header Type Selection */}
          <div style={{ marginBottom: '12px' }}>
            <div
              style={radioButtonStyle}
              onClick={() => setFormData(prev => ({ ...prev, headerType: 'text' }))}
            >
              <div style={formData.headerType === 'text' ? selectedRadioStyle : radioCircleStyle}>
                {formData.headerType === 'text' && <div style={{ width: '8px', height: '8px', background: '#ffffff', borderRadius: '50%' }} />}
              </div>
              <span style={{ fontSize: '16px' }}>⌨️</span>
              <span style={{ fontSize: '14px', color: '#495057' }}>Text</span>
            </div>
            
            <div
              style={radioButtonStyle}
              onClick={() => setFormData(prev => ({ ...prev, headerType: 'media' }))}
            >
              <div style={formData.headerType === 'media' ? selectedRadioStyle : radioCircleStyle}>
                {formData.headerType === 'media' && <div style={{ width: '8px', height: '8px', background: '#ffffff', borderRadius: '50%' }} />}
              </div>
              <span style={{ fontSize: '16px' }}>📤</span>
              <span style={{ fontSize: '14px', color: '#495057' }}>Media</span>
            </div>
          </div>

          {/* Header Text Input */}
          {formData.headerType === 'text' && (
            <input
              type="text"
              value={formData.headerText}
              onChange={e => setFormData(prev => ({ ...prev, headerText: e.target.value }))}
              placeholder="Provide text for header"
              style={inputStyle}
            />
          )}
        </div>

        {/* Message Body Section */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Message Body
          </label>
          
          {/* Dynamic Content Buttons */}
          <div style={{ marginBottom: '12px' }}>
            <button style={dynamicButtonStyle}>
              <span>⚙️</span>
              <span>Custom</span>
              <span>▼</span>
            </button>
            <button style={dynamicButtonStyle}>
              <span>👤</span>
              <span>Name</span>
            </button>
          </div>

          {/* Message Body Textarea */}
          <div style={{ position: 'relative' }}>
            <textarea
              value={formData.messageBody}
              onChange={e => setFormData(prev => ({ ...prev, messageBody: e.target.value }))}
              placeholder="Enter your message body here..."
              style={textareaStyle}
            />
            <button
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'none',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#6c757d'
              }}
              onClick={() => {
                // Emoji picker functionality would go here
                console.log('Emoji picker clicked');
              }}
            >
              😊
            </button>
          </div>
        </div>

        {/* Message Footer Section */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Message Footer (Optional)
          </label>
          <input
            type="text"
            value={formData.footerText}
            onChange={e => setFormData(prev => ({ ...prev, footerText: e.target.value }))}
            placeholder="Provide text for footer"
            style={inputStyle}
          />
        </div>

        {/* Delay Setting */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Delay in reply - {formData.delay} sec
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="range"
              min="0"
              max="30"
              value={formData.delay}
              onChange={e => setFormData(prev => ({ ...prev, delay: parseInt(e.target.value) }))}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '14px', color: '#6c757d', minWidth: '50px' }}>
              {formData.delay} sec
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button
            onClick={handleSave}
            style={{ ...buttonStyle, display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            💾 Save
          </button>
          <button
            onClick={onClose}
            style={{ ...secondaryButtonStyle, display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            ✕ Close
          </button>
        </div>
      </div>
    </div>
  );
} 